<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-header">
        <div class="login-logo-box">
          <img class="login-logo" src="@/assets/qm-replace-logo.png" alt="">
        </div>
        <h2>System Login</h2>
        <p>관리자 및 고객 시스템 접속</p>
      </div>

      <div class="login-form">
        <input
          v-model="username"
          placeholder="ID"
          @keyup.enter="login"
        />

        <!-- 🔥 비밀번호 + 눈 아이콘 -->
        <div class="password-wrapper">
          <input
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="Password"
            @keyup.enter="login"
          />

          <i
            :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
            class="toggle-eye"
            @click="showPassword = !showPassword"
          />
        </div>

        <button @click="login" :disabled="loading">
          {{ loading ? '로그인 중...' : '로그인' }}
        </button>

        <p v-if="errorMessage" class="error-text">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const username = ref('')
const password = ref('')
const showPassword = ref(false) // 🔥 추가
const loading = ref(false)
const errorMessage = ref('')

async function login() {
  errorMessage.value = ''

  if (!username.value.trim()) {
    errorMessage.value = '아이디를 입력해주세요.'
    return
  }

  if (!password.value.trim()) {
    errorMessage.value = '비밀번호를 입력해주세요.'
    return
  }

  try {
    loading.value = true

    const result = await authStore.login({
      username: username.value.trim(),
      password: password.value
    })

    const roleCode = result?.user?.roleCode

    if (['SUPER_ADMIN', 'ADMIN', 'OPERATOR'].includes(roleCode)) {
      await router.push({
        path: '/admin',
        query: { menu: 'siteList' }
      })
      return
    }

    if (['USER', 'MONITOR'].includes(roleCode)) {
      await router.push('/client')
      return
    }

    authStore.logout()
    errorMessage.value = '권한 정보가 올바르지 않습니다.'
  } catch (e) {
    console.error('login error', e)

    const serverMessage =
      e?.response?.data?.message ||
      e?.response?.data?.msg ||
      '아이디 또는 비밀번호가 올바르지 않습니다.'

    errorMessage.value = serverMessage
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;

  /* 🔥 배경 핵심 */
  background: url('@/assets/image/background-image-2.2.jpg') no-repeat center center;
  background-size: cover;

  /* 🔥 어두운 오버레이 (핵심) */
  position: relative;
}

.login-page::before {
  content: '';
  position: absolute;
  inset: 0;

  background: rgba(0, 0, 0, 0.35); /* 살짝 어둡게 */

  z-index: 0;
}

.login-card {
  position: relative;
  z-index: 1;

  width: 360px;

  /* 🔥 반투명 카드 */
  background: rgba(255, 255, 255, 0.92);

  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.4);

  /* 🔥 그림자 강화 */
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.25),
    0 4px 12px rgba(0, 0, 0, 0.1);

  backdrop-filter: blur(8px);

  overflow: hidden;
}

.login-header {
  padding: 28px 24px 20px;
  text-align: center;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.login-header h2 {
  margin-top: 10px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.login-header p {
  margin-top: 6px;
  font-size: 13px;
  color: var(--color-text-sub);
}

.login-form {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

input {
  height: 42px;
  padding: 0 12px;
  font-size: 14px;
  border-radius: 6px;

  border: 1px solid var(--color-border);
  background-color: var(--color-white);
  color: var(--color-text-main);

  transition: border-color 0.2s, box-shadow 0.2s;
}

input::placeholder {
  color: var(--color-text-sub);
}

input:focus {
  outline: none;
  border-color: var(--color-primary-border);
  box-shadow: 0 0 0 2px var(--color-primary-soft);
}

/* 🔥 비밀번호 영역 */
.password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-wrapper input {
  width: 100%;
  padding-right: 40px;
}

.toggle-eye {
  position: absolute;
  right: 10px;
  cursor: pointer;
  font-size: 16px;
  color: var(--color-text-sub);
}

.toggle-eye:hover {
  color: var(--color-text-main);
}

button {
  margin-top: 6px;
  height: 44px;
  border-radius: 6px;
  border: none;

  display: flex;
  align-items: center;
  justify-content: center;

  background-color: var(--color-primary);
  color: var(--color-white);

  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  transition: background-color 0.2s, transform 0.1s;
}

button:hover:not(:disabled) {
  background-color: var(--color-primary-border);
  transform: translateY(-1px);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-text {
  margin: 4px 0 0;
  font-size: 13px;
  color: #d93025;
  text-align: center;
}

.login-logo-box {
  padding: 10px;
}

.login-logo {
  width: 155px;
  height: 72px;
  margin: 0 auto;
}
</style>